import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Fade,
  Grow,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CommentIcon from '@mui/icons-material/Comment';
import SendIcon from '@mui/icons-material/Send';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchBlog,
  fetchBlogCommentsThunk,
  addBlogCommentThunk,
  toggleBlogLikeThunk,
  fetchBlogLikesThunk,
} from '../store/thunks/blogsThunks';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { trackBlogViewed } from '../services/analytics/firebaseAnalytics';
import LoginModal from '../components/auth/LoginModal';
import SEO from '../components/SEO';

const BlogDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedBlog, loading, error, blogComments, userLikes } = useAppSelector((state) => state.blogs);
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  useEffect(() => {
    if (slug) {
      dispatch(fetchBlog(slug));
    }
  }, [slug, dispatch]);

  useEffect(() => {
    if (selectedBlog) {
      dispatch(fetchBlogCommentsThunk(selectedBlog.id));
      dispatch(fetchBlogLikesThunk(selectedBlog.id));
      trackBlogViewed(selectedBlog.id, selectedBlog.title);
    }
  }, [selectedBlog, dispatch]);

  const handleLike = () => {
    if (!isAuthenticated || !user || !selectedBlog) {
      return;
    }
    dispatch(toggleBlogLikeThunk({ blogId: selectedBlog.id, userId: user.uid }));
    // Track like event
    if (!isLiked) {
      import('../services/analytics/firebaseAnalytics').then(({ trackBlogLiked }) => {
        trackBlogLiked(selectedBlog.id);
      });
    }
  };

  const handleShare = () => {
    if (!selectedBlog) return;
    
    const shareMethod = navigator.share ? 'native' : 'clipboard';
    
    if (navigator.share) {
      navigator.share({
        title: selectedBlog.title,
        text: selectedBlog.excerpt,
        url: window.location.href,
      }).then(() => {
        import('../services/analytics/firebaseAnalytics').then(({ trackBlogShared }) => {
          trackBlogShared(selectedBlog.id, selectedBlog.title, shareMethod);
        });
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        import('../services/analytics/firebaseAnalytics').then(({ trackBlogShared }) => {
          trackBlogShared(selectedBlog.id, selectedBlog.title, shareMethod);
        });
      });
    }
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim() || !isAuthenticated || !user || !selectedBlog || submittingComment) {
      return;
    }

    setSubmittingComment(true);
    try {
      await dispatch(
        addBlogCommentThunk({
          blogId: selectedBlog.id,
          userId: user.uid,
          userEmail: user.email,
          userName: user.displayName || user.email || 'Anonymous',
          comment: commentText.trim(),
        })
      ).unwrap();
      setCommentText('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !selectedBlog) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{error || 'Blog post not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/blog')} sx={{ mt: 2 }}>
          Back to Blog
        </Button>
      </Container>
    );
  }

  const comments = blogComments[selectedBlog.id] || [];
  const isLiked = userLikes[selectedBlog.id] || false;

  const siteUrl = 'https://learn2skill.com';
  const blogUrl = `${siteUrl}/blog/${selectedBlog.slug}`;
  const imageUrl = selectedBlog.featuredImage || `${siteUrl}/og-image.jpg`;

  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: selectedBlog.title,
    description: selectedBlog.excerpt,
    image: imageUrl,
    datePublished: selectedBlog.publishedAt || selectedBlog.createdAt,
    dateModified: selectedBlog.updatedAt || selectedBlog.publishedAt || selectedBlog.createdAt,
    author: {
      '@type': 'Person',
      name: selectedBlog.authorName || 'Learn2Skill Team',
      url: `${siteUrl}/author/${selectedBlog.authorId}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Learn2Skill',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': blogUrl,
    },
    articleSection: selectedBlog.category,
    keywords: selectedBlog.tags.join(', '),
    wordCount: selectedBlog.content.split(/\s+/).length,
    timeRequired: `PT${selectedBlog.readingTime || 5}M`,
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${siteUrl}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: selectedBlog.title,
        item: blogUrl,
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${selectedBlog.title} | Learn2Skill Blog`}
        description={selectedBlog.excerpt}
        keywords={selectedBlog.tags.join(', ')}
        image={imageUrl}
        url={`/blog/${selectedBlog.slug}`}
        type="article"
        author={selectedBlog.authorName || 'Learn2Skill Team'}
        publishedTime={selectedBlog.publishedAt || selectedBlog.createdAt}
        modifiedTime={selectedBlog.updatedAt || selectedBlog.publishedAt || selectedBlog.createdAt}
        canonicalUrl={blogUrl}
        structuredData={[articleStructuredData, breadcrumbStructuredData]}
      />
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Back Button */}
      <Fade in timeout={400}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4 }}
        >
          Back to Blog
        </Button>
      </Fade>

      {/* Header */}
      <Fade in timeout={600}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            <Chip
              label={selectedBlog.category}
              sx={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.3)',
                fontWeight: 600,
                color: 'primary.light',
              }}
            />
            {selectedBlog.tags.map((tag, idx) => (
              <Chip
                key={idx}
                label={tag}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'rgba(148, 163, 184, 0.3)',
                  color: 'text.secondary',
                }}
              />
            ))}
          </Box>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              mb: 3,
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {selectedBlog.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'primary.main',
                }}
              >
                {selectedBlog.authorName?.charAt(0).toUpperCase() || 'A'}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {selectedBlog.authorName || 'Anonymous'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(selectedBlog.publishedAt)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {selectedBlog.readingTime || 5} min read
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VisibilityIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {selectedBlog.views || 0} views
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <IconButton
              onClick={handleLike}
              disabled={!isAuthenticated}
              sx={{
                color: isLiked ? 'secondary.main' : 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(236, 72, 153, 0.1)',
                },
              }}
            >
              <FavoriteIcon />
            </IconButton>
            <Typography variant="body2" sx={{ alignSelf: 'center', mr: 2 }}>
              {selectedBlog.likes || 0}
            </Typography>
            <IconButton
              onClick={handleShare}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(99, 102, 241, 0.1)',
                },
              }}
            >
              <ShareIcon />
            </IconButton>
          </Box>
        </Box>
      </Fade>

      <Divider sx={{ my: 4 }} />

      {/* Featured Image */}
      {selectedBlog.featuredImage && (
        <Fade in timeout={800}>
          <Box
            sx={{
              mb: 4,
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            }}
          >
            <img
              src={selectedBlog.featuredImage}
              alt={selectedBlog.title}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          </Box>
        </Fade>
      )}

      {/* Content */}
      <Fade in timeout={1000}>
        <Box
          sx={{
            '& p': {
              mb: 2,
              lineHeight: 1.8,
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              mt: 4,
              mb: 2,
              fontWeight: 600,
            },
            '& code': {
              background: 'rgba(99, 102, 241, 0.1)',
              padding: '2px 6px',
              borderRadius: 2,
              fontFamily: 'monospace',
            },
            '& pre': {
              background: 'rgba(15, 23, 42, 0.8)',
              padding: 2,
              borderRadius: 2,
              overflow: 'auto',
            },
          }}
        >
          <MarkdownRenderer content={selectedBlog.content} />
        </Box>
      </Fade>

      <Divider sx={{ my: 6 }} />

      {/* Comments Section */}
      <Fade in timeout={1200}>
        <Box>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CommentIcon />
            Comments ({comments.length})
          </Typography>

          {/* Comment Form */}
          {isAuthenticated ? (
            <Paper
              sx={{
                p: 3,
                mb: 4,
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(148, 163, 184, 0.1)',
              }}
            >
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Write a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={handleSubmitComment}
                disabled={!commentText.trim() || submittingComment}
              >
                {submittingComment ? 'Posting...' : 'Post Comment'}
              </Button>
            </Paper>
          ) : (
            <Paper
              sx={{
                p: 3,
                mb: 4,
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(148, 163, 184, 0.1)',
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Please log in to leave a comment
              </Typography>
              <Button variant="contained" onClick={() => setLoginModalOpen(true)}>
                Log In
              </Button>
            </Paper>
          )}

          {/* Comments List */}
          {comments.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {comments.map((comment, index) => (
                <Grow in timeout={800 + index * 100} key={comment.id}>
                  <Paper
                    sx={{
                      p: 3,
                      background: 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid rgba(148, 163, 184, 0.1)',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: 'primary.main',
                        }}
                      >
                        {comment.userName?.charAt(0).toUpperCase() || 'A'}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {comment.userName || 'Anonymous'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(comment.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {comment.comment}
                    </Typography>
                  </Paper>
                </Grow>
              ))}
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No comments yet. Be the first to comment!
            </Typography>
          )}
        </Box>
      </Fade>

      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onSuccess={() => {
          // User successfully logged in, modal will close automatically
        }}
      />
    </Container>
    </>
  );
};

export default BlogDetailPage;

